import { useEffect, useRef, useState } from 'react';
import type { ImgHTMLAttributes } from 'react';

// IntersectionObserver is unavailable in jsdom and very old browsers. When it is
// missing we fall back to loading the image immediately so content never goes
// permanently blank.
function hasIntersectionObserver(): boolean {
  return typeof window !== 'undefined' && typeof window.IntersectionObserver === 'function';
}

/**
 * A drop-in replacement for `<img>` that defers loading below-the-fold images
 * until they are about to scroll into view, using the IntersectionObserver API.
 *
 * Deferring off-screen images cuts initial network contention and speeds up the
 * Largest Contentful Paint. Always pass explicit `width`/`height` so the browser
 * can reserve space before the pixels arrive — this is what keeps Cumulative
 * Layout Shift near zero.
 *
 * Above-the-fold / LCP images (a header logo, a hero) must NOT be lazy-loaded —
 * doing so delays the very paint we are trying to optimise. Pass `eager` for
 * those; the component then loads them straight away with a high fetch priority.
 */
interface LazyImageProps extends ImgHTMLAttributes<HTMLImageElement> {
  src: string;
  alt: string;
  /** Load immediately (above-the-fold / LCP images) instead of lazily. */
  eager?: boolean;
  /** IntersectionObserver root margin controlling how early loading begins. */
  rootMargin?: string;
}

export default function LazyImage({
  src,
  alt,
  width,
  height,
  eager = false,
  rootMargin = '200px',
  className,
  ...rest
}: LazyImageProps): JSX.Element {
  const imgRef = useRef<HTMLImageElement>(null);
  // Eager images (and environments without IntersectionObserver) load up front;
  // everything else waits until the observer reports it is near the viewport.
  const [visible, setVisible] = useState(() => eager || !hasIntersectionObserver());

  // `fetchpriority` has no React 18 prop mapping, so set it on the DOM node
  // directly rather than as a camelCase JSX prop (which React drops with a warning).
  useEffect(() => {
    const node = imgRef.current;
    if (node) {
      node.setAttribute('fetchpriority', eager ? 'high' : 'low');
    }
  }, [eager]);

  useEffect(() => {
    if (visible) {
      return undefined;
    }
    const node = imgRef.current;
    if (!node) {
      return undefined;
    }
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setVisible(true);
            observer.disconnect();
          }
        });
      },
      { rootMargin }
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [visible, rootMargin]);

  return (
    <img
      ref={imgRef}
      src={visible ? src : undefined}
      alt={alt}
      width={width}
      height={height}
      className={className}
      decoding="async"
      loading={eager ? 'eager' : 'lazy'}
      {...rest}
    />
  );
}
